import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { fetchProducts, fetchCategories } from "../lib/api";
import ProductCard from "../components/ProductCard";
import CategoryFilters from "../components/CategoryFilters";
import Seo from "../components/Seo";
import { CATEGORY_META } from "../lib/categoryMeta";
import { Link } from "react-router-dom";

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const initialCat = params.get("category") || "All";
  const [active, setActive] = useState(initialCat);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories().then(setCategories).catch(console.error);
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchProducts(active)
      .then(setProducts)
      .catch(console.error)
      .finally(() => setLoading(false));
    if (active === "All") {
      params.delete("category");
    } else {
      params.set("category", active);
    }
    setParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16" data-testid="shop-page">
      <Seo
        title={active === "All" ? "Shop All Merch" : `${active} — Shop`}
        path={active === "All" ? "/shop" : `/shop?category=${encodeURIComponent(active)}`}
        description={
          active === "All"
            ? "Browse every piece of Anthony's Playworld merch — t-shirts, hoodies, hats, mugs, stickers, plushies and posters. Bright colors, kid-tested comfort, adventure-ready."
            : `Shop ${active} from Anthony's Playworld — bold designs, kid-friendly comfort, and shipping straight to your door. Explore the full ${active.toLowerCase()} collection.`
        }
      />
      <span className="pw-badge pink mb-3 inline-block">SHOP THE DROP</span>
      <h1 className="font-display text-5xl sm:text-6xl uppercase leading-none mb-6">
        All Merch
      </h1>
      <p className="max-w-2xl mb-8 text-lg">
        Find your new favorite tee, hoodie or plushie. Browse by vibe below.
      </p>

      <div className="pw-card p-5 mb-8 bg-[#FFFDF0] text-sm leading-relaxed max-w-3xl">
        <p>
          Welcome to the full Anthony&apos;s Playworld collection — bright, playful
          apparel, drinkware, plushies and prints designed with real kid energy in mind.
          Every piece is officially licensed, printed in small batches, and shipped
          from the US with free standard delivery on orders over $50. Whether
          you&apos;re gifting a superfan, gearing up for a birthday party, or just
          adding a little Playworld magic to your everyday, you&apos;ll find something
          to love. Filter by category to zero in on your favorite drop.
        </p>
      </div>

      <CategoryFilters
        categories={categories}
        active={active}
        onChange={setActive}
      />

      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
        {loading && (
          <p data-testid="shop-loading" className="col-span-full text-center py-20 font-display text-2xl">
            Loading...
          </p>
        )}
        {!loading && products.length === 0 && (
          <p
            data-testid="shop-empty"
            className="col-span-full text-center py-20 font-display text-2xl"
          >
            No products here yet — check back soon!
          </p>
        )}
        {!loading &&
          products.map((p, i) => <ProductCard key={p.id} product={p} idx={i} />)}
      </div>

      {active !== "All" && CATEGORY_META[active] && (
        <section className="mt-16 pw-card p-6 sm:p-8" data-testid="category-details">
          <h2 className="font-display text-3xl uppercase mb-4">
            All About Playworld {active}
          </h2>
          <p className="mb-4 leading-relaxed">{CATEGORY_META[active].intro}</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
            <div className="p-4 border-3 border-black rounded-lg bg-[#FFD500]" style={{ borderWidth: 3 }}>
              <p className="font-display uppercase text-sm mb-1">Materials</p>
              <p className="text-sm">{CATEGORY_META[active].materials}</p>
            </div>
            <div className="p-4 border-3 border-black rounded-lg bg-[#00E5FF]" style={{ borderWidth: 3 }}>
              <p className="font-display uppercase text-sm mb-1">Care</p>
              <p className="text-sm">{CATEGORY_META[active].care}</p>
            </div>
            <div className="p-4 border-3 border-black rounded-lg bg-[#FF2E93] text-white" style={{ borderWidth: 3 }}>
              <p className="font-display uppercase text-sm mb-1">Fit</p>
              <p className="text-sm">{CATEGORY_META[active].fit}</p>
            </div>
          </div>
        </section>
      )}

      <section className="mt-12" data-testid="explore-more">
        <h2 className="font-display text-2xl uppercase mb-4">Explore More Categories</h2>
        <div className="flex flex-wrap gap-3">
          {categories.filter((c) => c !== active).map((c) => (
            <Link
              key={c}
              to={`/shop?category=${encodeURIComponent(c)}`}
              onClick={() => setActive(c)}
              className="pw-pill"
              data-testid={`explore-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              {c}
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
